import React, { useState } from 'react';
import APIService from '../services/api';
import ErpiaSelect from './ErpiaSelect';

/**
 * ENVOYER LE NDA À SIGNER — un seul endroit.
 *
 * Le geste existe à deux endroits : le menu de la carte candidat d'un besoin,
 * et le process de qualification. Deux implémentations, ce serait deux
 * endroits où corriger la même chose — et surtout deux comportements
 * différents devant les trois cas qui ne sont PAS des erreurs techniques :
 *
 *  1. pas d'email sur la fiche (`need_email`) → on DEMANDE l'adresse, on
 *     n'échoue pas ;
 *  2. adresse refusée par le serveur (400) → on rouvre le panneau avec ce
 *     qui a été tapé, au lieu de tout perdre ;
 *  3. enveloppe préparée (`prepare_url`) → la fenêtre de placement s'ouvre,
 *     et le NDA n'est PAS encore parti : le message le dit.
 *
 * DEUX CHOIX, PAS PLUS (24.09). Le panneau a porté un temps un éditeur de
 * paragraphes : le texte du modèle, relisible et modifiable avant l'envoi.
 * Retiré — « je veux juste choisir le template et ensuite ça m'ouvre le
 * truc ». Un NDA est un document CONTRACTUEL : il se corrige dans son modèle
 * Word, une fois, pas à chaque envoi. Ce qui reste ici est ce qui change d'un
 * envoi à l'autre : QUEL modèle (3 mois, 6 mois…) et À QUI.
 *
 * Le panneau DIT quand même ce que le serveur a rempli tout seul (le nom, la
 * date, le client) : le vérifier avant d'envoyer coûte un coup d'œil, et
 * s'apercevoir après coup qu'un NDA est parti sans le nom du client coûte un
 * document signé à refaire.
 *
 * Un NDA est un email sortant : il ne part jamais sans montrer à qui.
 * L'adresse reprise de la fiche reste modifiable, et la fiche n'est pas
 * réécrite au passage.
 */
const BASE = 'https://besoins-tat-production.up.railway.app';
const BI = ({ icon, style }) => <i className={`bi bi-${icon}`} style={style} />;
const authH = () => ({ Authorization: `Bearer ${APIService.getToken()}` });
const emailValide = (v) => /.+@.+\..+/.test(String(v || '').trim());

export function useEnvoiNda({ candidateId, besoinId, emailConnu, showNotification, onEnvoye }) {
  const [ask, setAsk] = useState(null);   // { email, deFiche, charge } | null
  const [occupe, setOccupe] = useState(false);
  // { etat: 'charge'|'pret'|'indispo'|'erreur', modeles, modeleId, client…, erreur }
  const [redac, setRedac] = useState(null);

  // Les modèles de l'organisation, et ce que le serveur saura remplir. Le
  // texte n'est plus rendu ici — seulement de quoi choisir et vérifier.
  const chargerApercu = async (templateId = null) => {
    if (candidateId == null) return;
    setRedac(r => ({ ...(r || {}), etat: 'charge', erreur: null }));
    try {
      const r = await fetch(`${BASE}/candidates/${candidateId}/nda/apercu`, {
        method: 'POST', headers: { ...authH(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ besoin_id: besoinId, ...(templateId != null ? { template_id: templateId } : {}) }),
      });
      const j = await r.json().catch(() => ({}));
      // Un 404 SANS notre réponse = la route n'existe pas encore sur ce
      // serveur : l'envoi marche comme avant, sans choix de modèle.
      if (r.status === 501 || (r.status === 404 && j.success !== false)) { setRedac({ etat: 'indispo' }); return; }
      if (!r.ok || j.success === false) { setRedac(x => ({ ...(x || {}), etat: 'erreur', erreur: j.error || `Erreur ${r.status}` })); return; }
      const d = j.data || {};
      setRedac({
        etat: 'pret', modeles: d.modeles || [], modeleId: d.modele_id ?? 'defaut',
        // CE QUI N'A PAS PU ÊTRE REMPLI — dit par le serveur, affiché tel quel
        // (vécu 24.09 : le nom du client manquait, et il ne restait qu'un blanc
        // dans le document ; on ne pouvait pas savoir d'où venait le trou).
        client: d.client ?? null, clientEtat: d.client_etat || null, clientMessage: d.client_message || null,
      });
    } catch (e) {
      setRedac(x => ({ ...(x || {}), etat: 'erreur', erreur: e.message }));
    }
  };

  // La ligne d'un besoin ne porte pas toujours l'email : quand elle ne l'a
  // pas, on le lit sur la fiche canonique avant de le demander — la fiche
  // reste la source de vérité.
  const ouvrir = async () => {
    const initial = String(emailConnu || '').trim();
    setAsk({ email: initial, deFiche: !!initial, charge: !initial && candidateId != null });
    if (!redac) chargerApercu(null);
    if (initial || candidateId == null) return;
    try {
      const r = await fetch(`${BASE}/candidats/vue/${candidateId}`, { headers: authH() });
      const j = await r.json().catch(() => ({}));
      const cand = j.data || j.candidat || j;
      const em = (cand?.email || '').trim();
      setAsk(a => a ? { ...a, charge: false, email: a.email || em, deFiche: a.deFiche || (!a.email && !!em) } : a);
    } catch { setAsk(a => a ? { ...a, charge: false } : a); }
  };

  const fermer = () => { setAsk(null); setRedac(null); };

  // Changer de modèle ne coûte plus rien à personne : il n'y a aucun texte
  // saisi à perdre. On recharge, sans confirmation à cliquer.
  const changerModele = (id) => chargerApercu(id);

  const envoyer = async (email) => {
    if (candidateId == null) { showNotification?.('Fiche pas encore synchronisée — réessayez dans un instant.', 'error'); return; }
    setOccupe(true);
    const pret = redac?.etat === 'pret';
    try {
      const r = await fetch(`${BASE}/candidates/${candidateId}/nda`, {
        method: 'POST', headers: { ...authH(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          besoin_id: besoinId, ...(email ? { email } : {}),
          ...(pret ? { template_id: redac.modeleId } : {}),
        }),
      });
      const j = await r.json().catch(() => ({}));
      if (j.need_email) {
        setOccupe(false);
        setAsk({ email: (email || emailConnu || '').trim(), deFiche: false, charge: false });
        showNotification?.('Précisez l\'adresse du signataire pour envoyer le NDA.', 'error');
        return;
      }
      // Refus métier (adresse invalide, modèle illisible…) : le serveur écrit
      // ses messages pour l'utilisateur — on les affiche tels quels et on
      // ROUVRE le panneau, au lieu de laisser recommencer à zéro.
      if (r.status === 400 || r.status === 404 || r.status === 422) {
        setOccupe(false);
        showNotification?.(j.error || j.message || 'Envoi refusé.', 'error');
        setAsk({ email: (email || '').trim(), deFiche: false, charge: false });
        return;
      }
      if (!r.ok || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
      if (j.prepare_url) window.open(j.prepare_url, 'nda', 'width=980,height=760');
      showNotification?.(j.message || 'NDA préparé — finalisez l\'envoi dans la fenêtre ouverte.', 'success');
      setRedac(null);
      onEnvoye?.(j);
    } catch (e) { showNotification?.('Erreur NDA : ' + e.message, 'error'); }
    finally { setOccupe(false); }
  };

  const valider = () => { const em = ask.email.trim(); setAsk(null); envoyer(em); };

  const etiquette = { marginTop: '14px', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-3)', marginBottom: '5px' };
  const seulDefaut = redac?.etat === 'pret' && (redac.modeles || []).length <= 1;

  const panneau = !ask ? null : (
    <div onClick={fermer} style={{ position: 'fixed', inset: 0, zIndex: 960, background: 'rgba(5,6,8,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
      <div onClick={e => e.stopPropagation()} role="dialog" aria-label="Envoyer le NDA"
        style={{ width: 'min(460px, 96vw)', maxHeight: '92vh', overflowY: 'auto', background: 'var(--panel)', border: '1px solid var(--border)', borderRadius: '14px', padding: '18px 20px' }}>
        <div style={{ fontSize: '15.5px', fontWeight: 700, color: 'var(--text-strong)' }}>Envoyer le NDA</div>
        <div style={{ marginTop: '3px', fontSize: '12.5px', color: 'var(--text-2)', lineHeight: 1.5 }}>
          {redac && redac.etat !== 'indispo'
            ? 'Choisissez le modèle, puis envoyez-le en signature.'
            : 'Le NDA de cette opportunité part en signature à l\'adresse ci-dessous.'}
        </div>

        {redac && redac.etat !== 'indispo' && (<>
          <div style={etiquette}>Modèle</div>
          <ErpiaSelect className="erp-field" value={String(redac.modeleId ?? 'defaut')} disabled={redac.etat === 'charge'}
            aria-label="Modèle de NDA" onChange={e => changerModele(e.target.value)}>
            {(redac.modeles || [{ id: 'defaut', nom: 'Modèle par défaut' }]).map(m => (
              <option key={m.id} value={String(m.id)}>{m.nom}</option>
            ))}
          </ErpiaSelect>
          {redac.etat === 'charge' && (
            <div role="status" style={{ marginTop: '6px', fontSize: '11.5px', color: 'var(--text-2)' }}>
              <BI icon="arrow-repeat" style={{ animation: 'ats-spin .8s linear infinite', display: 'inline-block' }} /> Préparation du NDA…
            </div>
          )}
          {redac.etat === 'erreur' && (
            <div role="alert" style={{ marginTop: '6px', fontSize: '12px', color: 'var(--erp-danger)' }}>
              {redac.erreur}
              <div style={{ marginTop: '6px' }}>
                <button type="button" className="erp-btn erp-btn--secondary erp-btn--sm" onClick={() => chargerApercu(redac.modeleId ?? null)}>Réessayer</button>
              </div>
            </div>
          )}
          {seulDefaut && redac.etat === 'pret' && (
            <div style={{ marginTop: '5px', fontSize: '11.5px', color: 'var(--text-3)', lineHeight: 1.5 }}>
              Un seul modèle pour l'instant. Déposez vos variantes (NDA 3 mois, 6 mois…) dans les modèles de l'organisation, type NDA.
            </div>
          )}

          {/* CE QUI EST REMPLI TOUT SEUL — vérifiable AVANT l'envoi. Le client
              manquant ne se voyait qu'une fois le document signé. */}
          {redac.etat === 'pret' && redac.clientEtat === 'vide' && redac.clientMessage && (
            <div role="alert" style={{ marginTop: '7px', padding: '8px 10px', borderRadius: '9px',
              border: '1px solid var(--erp-warning)', color: 'var(--erp-warning)',
              fontSize: '11.5px', lineHeight: 1.5 }}>
              <BI icon="exclamation-triangle" /> {redac.clientMessage}
            </div>
          )}
          {redac.etat === 'pret' && redac.clientEtat === 'rempli' && redac.client && (
            <div style={{ marginTop: '6px', fontSize: '11.5px', color: 'var(--text-3)', lineHeight: 1.5 }}>
              Le nom du candidat, la date et le client (<b>{redac.client}</b>) sont remplis dans le document.
            </div>
          )}
        </>)}

        <div style={etiquette}>Email du signataire</div>
        <input value={ask.email} type="email" autoFocus placeholder="prenom.nom@email.com" className="erp-field"
          onChange={e => setAsk(a => ({ ...a, email: e.target.value }))}
          onKeyDown={e => { if (e.key === 'Enter' && emailValide(ask.email) && redac?.etat !== 'charge') valider(); if (e.key === 'Escape') fermer(); }}
          style={{ width: '100%', boxSizing: 'border-box' }} />
        <div style={{ marginTop: '6px', fontSize: '11.5px', color: 'var(--text-3)', lineHeight: 1.5 }}>
          {ask.charge
            ? 'Lecture de l\'adresse sur la fiche candidat…'
            : ask.deFiche
              ? 'Adresse reprise de la fiche candidat — modifiable avant l\'envoi. La fiche n\'est pas réécrite.'
              : 'Aucun email sur la fiche candidat — saisissez l\'adresse du signataire.'}
        </div>
        <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
          <button onClick={fermer} className="bd-mini">Annuler</button>
          <button disabled={!emailValide(ask.email) || redac?.etat === 'charge'} className="bd-mini bd-mini-accent"
            style={!emailValide(ask.email) || redac?.etat === 'charge' ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
            onClick={valider}>
            <BI icon="vector-pen" /> Envoyer le NDA
          </button>
        </div>
      </div>
    </div>
  );

  return { ouvrir, envoyer, occupe, panneau };
}

export default useEnvoiNda;
